import { useRef, useState } from "react";
import { useRouter } from "next/router";
import { toast } from "sonner";
import { RnX } from "@remi/icons/next";
import { useController } from "@remi/next-ui/hooks";
import type { NotificationListItem } from "@remi/types/notification/list";

import { cn } from "~/lib/utils";
import { NotificationItem } from "../inbox/list/NotificationItem";

type NotificationToastProps = {
  notification: NotificationListItem;
  eventId: string;
};

export const NotificationToast = ({ notification, eventId }: NotificationToastProps) => {
  const [dismissing, setDismissing] = useState(false);
  const router = useRouter();

  const startCoords = useRef<{ x: number; y: number } | null>(null);

  const controller = useController("idle", {
    idle: {
      onPointerDown: (event: React.PointerEvent) => {
        controller.set("interacting");
        startCoords.current = { x: event.pageX, y: event.pageY };
      },
    },
    interacting: {
      onPointerUp: async (event: React.PointerEvent) => {
        controller.set("idle");
        const endCoords = { x: event.pageX, y: event.pageY };

        if (startCoords.current === null) {
          return;
        }

        const distance = Math.hypot(endCoords.x - startCoords.current.x, endCoords.y - startCoords.current.y);
        startCoords.current = null;

        // Ignore if the pointer moved too much - probably dragging
        if (distance > 10) {
          return;
        }
        const toastElement = event.currentTarget.closest("[data-sonner-toast]");
        const isToastExpanded = toastElement?.getAttribute("data-expanded") === "true";
        const isOnlyToast = toast.getToasts().length === 1;

        // If collapsed, prevent navigation (let Sonner's hover expand it), the only toast will have isToastExpanded false
        if (!isToastExpanded && !isOnlyToast) {
          return;
        }

        toast.dismiss(notification.id);
        await router.push(`/notifications/${eventId}`);
        toast.dismiss();
      },
    },
  });

  return (
    <div className="flex w-screen max-w-full flex-col rounded-lg border border-border-tertiary bg-background-primary shadow-modal-md sm:max-w-72">
      <div className="toast-banner relative">
        <button
          className={cn(
            "close-all-button absolute -top-7 right-0 z-50 flex h-5 w-5 items-center justify-center rounded-full border border-border-primary bg-background-primary text-icon-secondary opacity-0 shadow-modal-md transition-all",
            dismissing && "w-10",
          )}
          onClick={dismissing ? () => toast.dismiss() : () => setDismissing(true)}
          onMouseLeave={() => setDismissing(false)}
          onBlur={() => setDismissing(false)}
        >
          {dismissing ? (
            <span className="text-nowrap px-1 text-text-secondary text-xxs ease-in">Clear</span>
          ) : (
            <RnX size="xs" className="ease-in" />
          )}
        </button>
      </div>
      <NotificationItem
        index={0}
        selected={false}
        notification={notification}
        isToast={true}
        onPointerDown={controller.onPointerDown}
        onPointerUp={controller.onPointerUp}
      />
    </div>
  );
};
